/**
 * Shared chrome for the pricing surfaces: /pricing, the Home pricing section and
 * the partner plans block on /partner. Every price on the site is read the same
 * way because all three render it through these primitives.
 *
 * Layout and type tokens only — no copy, no plan data.
 */
import React from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  ButtonBase,
  Typography
} from '@mui/material';
import {
  Add as AddIcon,
  Check as CheckIcon,
  Remove as RemoveIcon
} from '@mui/icons-material';
import { DOCUMENT, documentEyebrowSx, pageShellSx } from '../constants/documentTheme';

export const pricingWrapSx = {
  ...pageShellSx,
  boxSizing: 'border-box',
};

export const pricingPaperSx = {
  bgcolor: DOCUMENT.surface,
  border: `1px solid ${DOCUMENT.rule}`,
  p: { xs: 3, md: 5 },
  maxWidth: '100%',
  minWidth: 0,
  overflowX: 'hidden',
  boxSizing: 'border-box',
};

export const pricingHeadingSx = {
  fontFamily: DOCUMENT.display,
  fontWeight: 400,
  fontSize: { xs: 24, md: 31 },
  lineHeight: 1.18,
  letterSpacing: '-0.01em',
  color: DOCUMENT.ink,
  mb: 1.75
};

export const pricingSubSx = {
  color: DOCUMENT.ink3,
  fontSize: { xs: 14, md: 15 },
  lineHeight: 1.65,
  maxWidth: '44ch',
  mb: 3.75
};

export const pricingFootSx = {
  mt: 3.25,
  color: DOCUMENT.ink3,
  fontSize: 13.5,
  lineHeight: 1.65,
  maxWidth: '74ch'
};

export const pricingPrimaryButtonSx = {
  bgcolor: DOCUMENT.oxblood,
  color: DOCUMENT.ivory,
  borderRadius: 0,
  boxShadow: 'none',
  px: 3.5,
  py: 1.75,
  fontSize: 11.5,
  fontWeight: 500,
  letterSpacing: '0.13em',
  textTransform: 'uppercase',
  '&:hover': { bgcolor: DOCUMENT.nav, boxShadow: 'none' }
};

export const pricingLinkButtonSx = {
  color: DOCUMENT.ink,
  borderRadius: 0,
  px: 0,
  py: 0.25,
  minWidth: 0,
  fontSize: 11.5,
  fontWeight: 500,
  letterSpacing: '0.13em',
  textTransform: 'uppercase',
  borderBottom: `1px solid ${DOCUMENT.rule}`,
  '&:hover': { bgcolor: 'transparent', borderBottomColor: DOCUMENT.ink }
};

export const pricingOutlineButtonSx = {
  color: DOCUMENT.ink,
  border: `1px solid ${DOCUMENT.ink}`,
  borderRadius: 0,
  boxShadow: 'none',
  px: 3.5,
  py: 1.6,
  fontSize: 11.5,
  fontWeight: 500,
  letterSpacing: '0.13em',
  textTransform: 'uppercase',
  '&:hover': { bgcolor: DOCUMENT.ivory, borderColor: DOCUMENT.ink, boxShadow: 'none' }
};

export const billingToggleSx = (selected) => ({
  fontFamily: DOCUMENT.sans,
  fontSize: 11,
  fontWeight: 500,
  letterSpacing: '0.11em',
  textTransform: 'uppercase',
  px: 1.9,
  py: 0.9,
  bgcolor: DOCUMENT.surface,
  border: `1px solid ${selected ? DOCUMENT.oxblood : DOCUMENT.rule}`,
  color: selected ? DOCUMENT.oxblood : DOCUMENT.placeholder,
  '&:hover': { color: DOCUMENT.ink }
});

/**
 * Monthly/yearly billing switch shared across partner pricing surfaces.
 * The savings line only appears when yearly is selected — it is an annual
 * discount, not a trial.
 */
export const BillingIntervalToggle = ({
  value,
  onChange,
  ariaLabel,
  monthlyLabel,
  yearlyLabel,
  yearlySavingLabel,
  sx
}) => (
  <Box
    role="group"
    aria-label={ariaLabel}
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 1.5,
      flexWrap: 'wrap',
      ...sx
    }}
  >
    {[
      ['monthly', monthlyLabel],
      ['yearly', yearlyLabel]
    ].map(([interval, label]) => (
      <ButtonBase
        key={interval}
        aria-pressed={value === interval}
        onClick={() => onChange(interval)}
        sx={billingToggleSx(value === interval)}
      >
        {label}
      </ButtonBase>
    ))}
    {value === 'yearly' && yearlySavingLabel ? (
      <Typography sx={{ color: DOCUMENT.accent, fontSize: 12.5 }}>
        {yearlySavingLabel}
      </Typography>
    ) : null}
  </Box>
);

export const Eyebrow = ({ children }) => (
  <Typography sx={{ ...documentEyebrowSx, mb: 2.25 }}>{children}</Typography>
);

// Small ruled caps label, used for the plan a page is steering people towards.
export const PlanBadge = ({ children }) => (
  <Box
    component="span"
    sx={{
      display: 'inline-block',
      ml: 1.25,
      px: 0.9,
      py: '2px',
      fontSize: 9.5,
      fontWeight: 500,
      letterSpacing: '0.14em',
      textTransform: 'uppercase',
      color: DOCUMENT.accent,
      border: `1px solid ${DOCUMENT.accent}`,
      verticalAlign: '2px'
    }}
  >
    {children}
  </Box>
);

/**
 * One priced line: the plan on the left, the amount on the right, and the
 * feature list underneath so every public card is read the same way.
 */
export const PriceRow = ({ name, meta, subMeta, amount, unit, badge, free, features, onAmountClick, amountHref, amountAriaLabel }) => (
  <Box
    sx={{
      py: 2.5,
      borderTop: `1px solid ${DOCUMENT.rule}`
    }}
  >
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'baseline',
        gap: { xs: 2, md: 3 }
      }}
    >
      <Box>
        <Typography component="p" sx={{ fontWeight: 500, fontSize: 16, color: DOCUMENT.ink }}>
          {name}
          {badge && <PlanBadge>{badge}</PlanBadge>}
        </Typography>
        <Typography sx={{ mt: 0.5, color: DOCUMENT.ink3, fontSize: 13.5, lineHeight: 1.55 }}>
          {meta}
        </Typography>
        {subMeta && (
          <Typography sx={{ mt: 0.35, color: DOCUMENT.ink2, fontSize: 13.5, lineHeight: 1.55 }}>
            {subMeta}
          </Typography>
        )}
      </Box>
      {amountHref || onAmountClick ? (
        <Box
          component={amountHref ? 'a' : 'button'}
          type={amountHref ? undefined : 'button'}
          href={amountHref}
          onClick={amountHref ? undefined : onAmountClick}
          aria-label={amountAriaLabel}
          sx={{
            border: 0,
            bgcolor: 'transparent',
            p: 0,
            cursor: 'pointer',
            textAlign: 'right',
            textDecoration: 'none',
            color: 'inherit'
          }}
        >
          <PriceAmount amount={amount} unit={unit} free={free} />
        </Box>
      ) : (
        <PriceAmount amount={amount} unit={unit} free={free} />
      )}
    </Box>
    {Array.isArray(features) && features.length > 0 && (
      <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0, mt: 1.5 }}>
        {features.map((feature) => (
          <Box
            component="li"
            key={feature}
            sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 0.75 }}
          >
            <CheckIcon sx={{ fontSize: 15, color: DOCUMENT.accent, mt: 0.3, flexShrink: 0 }} />
            <Typography sx={{ fontSize: 13.5, color: DOCUMENT.ink2, lineHeight: 1.5 }}>
              {feature}
            </Typography>
          </Box>
        ))}
      </Box>
    )}
  </Box>
);

/**
 * A price set in the display face, with its billing unit trailing in the body
 * face. Not a heading: the plan name it belongs to is the heading.
 */
export const PriceAmount = ({ amount, unit, free, size }) => (
  <Typography
    component="p"
    sx={{
      fontFamily: DOCUMENT.display,
      fontWeight: 400,
      fontSynthesis: 'none',
      fontVariantNumeric: 'tabular-nums',
      fontSize: size || (free ? 19 : { xs: 24, md: 29 }),
      lineHeight: 1,
      whiteSpace: 'nowrap',
      color: free ? DOCUMENT.accent : DOCUMENT.ink
    }}
  >
    {amount}
    {unit && (
      <Box
        component="span"
        sx={{ fontFamily: DOCUMENT.sans, fontSize: 12.5, color: DOCUMENT.ink3, ml: 0.65 }}
      >
        {unit}
      </Box>
    )}
  </Typography>
);

export const PromiseBox = ({ title, children, sx = {} }) => (
  <Box sx={{ border: `1px solid ${DOCUMENT.accent}`, bgcolor: DOCUMENT.surface, p: { xs: 2.5, md: 3 }, mt: 3.5, ...sx }}>
    <Typography
      component="span"
      sx={{ display: 'block', fontFamily: DOCUMENT.display, fontSize: 19, mb: 0.75, color: DOCUMENT.ink }}
    >
      {title}
    </Typography>
    <Typography component="div" sx={{ fontSize: 14.5, lineHeight: 1.6, color: DOCUMENT.ink2 }}>
      {children}
    </Typography>
  </Box>
);

/**
 * Optional paid onboarding sits below the plan rows so self-serve signup stays
 * approachable while agencies that want hands-on help can buy it.
 */
export const EnterpriseCallout = ({ title, body, ctaHref, ctaLabel }) => (
  <Box
    sx={{
      mt: 2.5,
      pt: 2.5,
      borderTop: `1px solid ${DOCUMENT.rule}`
    }}
  >
    <Typography
      component="p"
      sx={{ fontFamily: DOCUMENT.display, fontSize: { xs: 17, md: 19 }, color: DOCUMENT.ink, mb: 0.75 }}
    >
      {title}
    </Typography>
    <Typography sx={{ fontSize: 14, lineHeight: 1.65, color: DOCUMENT.ink2, maxWidth: '72ch' }}>
      {body}
    </Typography>
    {ctaHref && ctaLabel && (
      <Button
        component="a"
        href={ctaHref}
        sx={{ ...pricingLinkButtonSx, mt: 1.75, display: 'inline-flex' }}
      >
        {ctaLabel}
      </Button>
    )}
  </Box>
);

/**
 * Optional paid onboarding sits below the plan rows so self-serve signup stays
 * approachable while agencies that want hands-on help can buy it.
 */
export const OnboardingCallout = ({
  eyebrow,
  title,
  body,
  enterpriseNote,
  ctaHref,
  ctaLabel,
  secondaryCtaHref,
  secondaryCtaLabel,
  onCtaClick,
  onSecondaryCtaClick,
}) => (
  <Box
    sx={{
      mt: 1.5,
      pt: 2.75,
      borderTop: `1px solid ${DOCUMENT.rule}`
    }}
  >
    {eyebrow ? (
      <Typography sx={{ ...documentEyebrowSx, mb: 1.25, color: DOCUMENT.placeholder }}>
        {eyebrow}
      </Typography>
    ) : null}
    <Typography
      component="p"
      sx={{ fontFamily: DOCUMENT.display, fontSize: { xs: 18, md: 21 }, color: DOCUMENT.ink, mb: 0.75 }}
    >
      {title}
    </Typography>
    <Typography sx={{ fontSize: 14.5, lineHeight: 1.65, color: DOCUMENT.ink2, maxWidth: '62ch' }}>
      {body}
    </Typography>
    {enterpriseNote ? (
      <Typography sx={{ fontSize: 13.5, lineHeight: 1.6, color: DOCUMENT.ink3, mt: 1.5, maxWidth: '62ch' }}>
        {enterpriseNote}
      </Typography>
    ) : null}
    {(ctaHref || onCtaClick || secondaryCtaHref || onSecondaryCtaClick) && (ctaLabel || secondaryCtaLabel) ? (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 2, alignItems: 'center' }}>
        {ctaLabel && (ctaHref || onCtaClick) ? (
          <Button
            component={ctaHref ? 'a' : 'button'}
            href={ctaHref || undefined}
            onClick={onCtaClick}
            sx={{ ...pricingLinkButtonSx, display: 'inline-flex' }}
          >
            {ctaLabel}
          </Button>
        ) : null}
        {secondaryCtaLabel && (secondaryCtaHref || onSecondaryCtaClick) ? (
          <Button
            component={secondaryCtaHref ? 'a' : 'button'}
            href={secondaryCtaHref || undefined}
            onClick={onSecondaryCtaClick}
            sx={{ ...pricingLinkButtonSx, display: 'inline-flex' }}
          >
            {secondaryCtaLabel}
          </Button>
        ) : null}
      </Box>
    ) : null}
  </Box>
);

export const CtaPair = ({ primary, onPrimary, secondary, onSecondary }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 2, md: 3.25 }, flexWrap: 'wrap', mt: 4.25 }}>
    <Button variant="contained" onClick={onPrimary} sx={pricingPrimaryButtonSx}>
      {primary}
    </Button>
    <Button onClick={onSecondary} sx={pricingLinkButtonSx}>
      {secondary}
    </Button>
  </Box>
);

const rateEyebrowSx = {
  ...documentEyebrowSx,
  mb: 1.25,
  color: DOCUMENT.placeholder
};

const RateCell = ({ label, value, condition, scale, emphasized }) => (
  <Box
    component="div"
    sx={{
      bgcolor: emphasized ? DOCUMENT.ivory : DOCUMENT.surface,
      p: { xs: 2.5, md: 3 }
    }}
  >
    <Typography component="dt" sx={rateEyebrowSx}>{label}</Typography>
    <Typography
      component="dd"
      sx={{
        m: 0,
        fontFamily: DOCUMENT.display,
        fontWeight: 400,
        fontSynthesis: 'none',
        fontVariantNumeric: 'tabular-nums',
        fontSize: String(value).length > 12 ? { xs: 17, md: 19 } : { xs: 34, md: 40 },
        lineHeight: String(value).length > 12 ? 1.25 : 1,
        color: emphasized ? DOCUMENT.accent : DOCUMENT.ink
      }}
    >
      {value}
    </Typography>
    <Typography sx={{ fontSize: 13.5, color: DOCUMENT.ink2, lineHeight: 1.65, mt: 1.4, maxWidth: '46ch' }}>
      {condition}
    </Typography>
    {scale && (
      <Typography sx={{ fontSize: 12.5, color: DOCUMENT.ink3, mt: 1.1, fontVariantNumeric: 'tabular-nums' }}>
        {scale}
      </Typography>
    )}
  </Box>
);

/**
 * Own-client 0% and the marketplace finder's fee, stated as a pair so one
 * cannot be read without the other.
 */
export const RatePair = ({ own, introduced }) => (
  <Box
    component="dl"
    sx={{
      display: 'grid',
      gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
      gap: '1px',
      bgcolor: DOCUMENT.ruleHard,
      border: `1px solid ${DOCUMENT.ruleHard}`,
      m: 0,
      mb: 3.25
    }}
  >
    <RateCell {...own} emphasized />
    <RateCell {...introduced} />
  </Box>
);

export const SummaryStrip = ({ items }) => (
  <Box
    component="dl"
    sx={{
      display: 'flex',
      flexWrap: 'wrap',
      borderTop: `1px solid ${DOCUMENT.rule}`,
      mt: 3.25,
      mb: 0
    }}
  >
    {items.map((item, index) => (
      <Box
        key={item.label}
        sx={{
          flex: { xs: '1 1 50%', md: 1 },
          py: 2,
          px: { xs: 2, md: 2.5 },
          ...(index === 0 ? { pl: { md: 0 } } : {}),
          borderRight: { md: `1px solid ${DOCUMENT.rule}` },
          borderBottom: { xs: `1px solid ${DOCUMENT.rule}`, md: 'none' },
          '&:last-of-type': { borderRight: 0 }
        }}
      >
        <Typography component="dt" sx={{ ...documentEyebrowSx, mb: 1, color: DOCUMENT.placeholder }}>
          {item.label}
        </Typography>
        <Typography
          component="dd"
          sx={{
            m: 0,
            fontSize: 19,
            fontVariantNumeric: 'tabular-nums',
            color: item.accent ? DOCUMENT.accent : DOCUMENT.ink
          }}
        >
          {item.value}
        </Typography>
      </Box>
    ))}
  </Box>
);

// Screen readers need to hear "included" where a sighted reader sees a tick, so
// the mark and its label travel together.
// Use px strings: MUI sx treats width/height `1` as 100%, which blew the page out.
const visuallyHiddenSx = {
  position: 'absolute',
  width: '1px',
  height: '1px',
  padding: 0,
  margin: '-1px',
  overflow: 'hidden',
  clip: 'rect(0, 0, 0, 0)',
  whiteSpace: 'nowrap',
  border: 0,
};

const ComparisonMark = ({ included, yesLabel, noLabel }) => (
  <>
    {included ? (
      <CheckIcon sx={{ fontSize: 17, color: DOCUMENT.accent }} aria-hidden="true" />
    ) : (
      <RemoveIcon sx={{ fontSize: 17, color: DOCUMENT.placeholder }} aria-hidden="true" />
    )}
    <Box component="span" sx={visuallyHiddenSx}>
      {included ? yesLabel : noLabel}
    </Box>
  </>
);

const comparisonCellSx = {
  position: 'relative',
  overflow: 'hidden',
  borderBottom: `1px solid ${DOCUMENT.rule}`,
  py: 1.6,
  px: { xs: 1.5, md: 2 },
  fontSize: 13.5,
  color: DOCUMENT.ink2,
  textAlign: 'center',
  verticalAlign: 'middle'
};

/**
 * A real table, because that is what this is: rows of capabilities read against
 * columns of plans. Cells hold `true`, `false`, or a string to print.
 *
 * Wide on purpose — four partner plans do not compress into a phone — so it
 * scrolls sideways rather than wrapping into something unreadable.
 */
export const ComparisonTable = ({ caption, columns, rows, yesLabel, noLabel }) => (
  <Box
    sx={{
      overflowX: 'auto',
      mt: 3.25,
      border: `1px solid ${DOCUMENT.rule}`,
      maxWidth: '100%',
      width: '100%',
      minWidth: 0,
      WebkitOverflowScrolling: 'touch',
    }}
  >
    <Box
      component="table"
      sx={{
        width: '100%',
        minWidth: { xs: columns.length > 3 ? 620 : 520, md: 0 },
        borderCollapse: 'collapse',
        bgcolor: DOCUMENT.surface
      }}
    >
      <Box component="caption" sx={visuallyHiddenSx}>
        {caption}
      </Box>
      <Box component="thead">
        <Box component="tr">
          <Box
            component="th"
            scope="col"
            sx={{
              ...comparisonCellSx,
              ...documentEyebrowSx,
              py: 1.75,
              width: '34%',
              textAlign: 'left',
              color: DOCUMENT.placeholder
            }}
          >
            {caption}
          </Box>
          {columns.map((column) => (
            <Box
              component="th"
              scope="col"
              key={column.id}
              sx={{
                ...comparisonCellSx,
                ...documentEyebrowSx,
                py: 1.75,
                textAlign: 'center',
                color: column.highlight ? DOCUMENT.accent : DOCUMENT.ink,
                bgcolor: column.highlight ? DOCUMENT.ivory : 'transparent'
              }}
            >
              {column.label}
            </Box>
          ))}
        </Box>
      </Box>
      <Box component="tbody">
        {rows.map((row) => (
          <Box component="tr" key={row.id}>
            <Box
              component="th"
              scope="row"
              sx={{
                ...comparisonCellSx,
                textAlign: 'left',
                fontWeight: 400,
                color: DOCUMENT.ink
              }}
            >
              {row.label}
            </Box>
            {row.cells.map((cell, index) => (
              <Box
                component="td"
                key={columns[index].id}
                sx={{
                  ...comparisonCellSx,
                  bgcolor: columns[index].highlight ? DOCUMENT.ivory : 'transparent'
                }}
              >
                {typeof cell === 'boolean' ? (
                  <ComparisonMark included={cell} yesLabel={yesLabel} noLabel={noLabel} />
                ) : (
                  cell
                )}
              </Box>
            ))}
          </Box>
        ))}
      </Box>
    </Box>
  </Box>
);

/**
 * The objections that otherwise arrive by email. Collapsed by default so the
 * prices stay the thing you see first, and marked up as headings so a screen
 * reader can move between questions without opening each one.
 */
export const FaqList = ({ items }) => (
  <Box sx={{ mt: 3.25, borderTop: `1px solid ${DOCUMENT.rule}` }}>
    {items.map((item) => (
      <Accordion
        key={item.id}
        square
        disableGutters
        elevation={0}
        sx={{
          bgcolor: 'transparent',
          borderBottom: `1px solid ${DOCUMENT.rule}`,
          '&::before': { display: 'none' }
        }}
      >
        <AccordionSummary
          expandIcon={<AddIcon sx={{ fontSize: 18, color: DOCUMENT.ink3 }} />}
          aria-controls={`faq-${item.id}-content`}
          id={`faq-${item.id}-header`}
          sx={{
            px: 0,
            '& .MuiAccordionSummary-content': { my: 2 },
            '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': { transform: 'rotate(45deg)' }
          }}
        >
          <Typography component="h3" sx={{ fontSize: 15, fontWeight: 500, color: DOCUMENT.ink, pr: 2 }}>
            {item.question}
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ px: 0, pt: 0, pb: 2.5 }}>
          <Typography sx={{ fontSize: 14, lineHeight: 1.7, color: DOCUMENT.ink2, maxWidth: '68ch' }}>
            {item.answer}
          </Typography>
        </AccordionDetails>
      </Accordion>
    ))}
  </Box>
);
